/*
 * Poly2Tri Copyright (c) 2009-2014, Poly2Tri Contributors
 * http://code.google.com/p/poly2tri/
 *
 * poly2tri.js (JavaScript port) (c) 2009-2014, Poly2Tri Contributors
 * https://github.com/r3mi/poly2tri.js
 *
 * All rights reserved.
 *
 * Distributed under the 3-clause BSD License, see LICENSE.txt
 */

import type { XY } from './xy'

/**
 * Precision to detect repeated or collinear points
 */
export const EPSILON = 1e-12

/**
 * Orientation enum for geometric calculations
 */
export enum Orientation {
	CW = 1,
	CCW = -1,
	COLLINEAR = 0
}

/**
 * Formula to calculate signed area
 * Positive if CCW
 * Negative if CW
 * 0 if collinear
 *
 * A[P1,P2,P3]  =  (x1*y2 - y1*x2) + (x2*y3 - y2*x3) + (x3*y1 - y3*x1)
 *              =  (x1-x3)*(y2-y3) - (y1-y3)*(x2-x3)
 */
export function orient2d(pa: XY, pb: XY, pc: XY): Orientation {
	const detleft = (pa.x - pc.x) * (pb.y - pc.y)
	const detright = (pa.y - pc.y) * (pb.x - pc.x)
	const val = detleft - detright

	if (val > -EPSILON && val < EPSILON) {
		return Orientation.COLLINEAR
	} else if (val > 0) {
		return Orientation.CCW
	} else {
		return Orientation.CW
	}
}

/**
 * Check if point is in scan area
 */
export function inScanArea(pa: XY, pb: XY, pc: XY, pd: XY): boolean {
	const oadb = (pa.x - pb.x) * (pd.y - pb.y) - (pd.x - pb.x) * (pa.y - pb.y)
	if (oadb >= -EPSILON) {
		return false
	}

	const oadc = (pa.x - pc.x) * (pd.y - pc.y) - (pd.x - pc.x) * (pa.y - pc.y)
	if (oadc <= EPSILON) {
		return false
	}
	return true
}

/**
 * Check if the angle between (pa,pb) and (pa,pc) is obtuse i.e. (angle > π/2 || angle < -π/2)
 */
export function isAngleObtuse(pa: XY, pb: XY, pc: XY): boolean {
	const ax = pb.x - pa.x
	const ay = pb.y - pa.y
	const bx = pc.x - pa.x
	const by = pc.y - pa.y
	return (ax * bx + ay * by) < 0
}

